import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "../assets/css/common.css";
import styles from "../assets/css/photo-permission.module.css";
import DocumentBody from "../components/DocumentBody";
import SignaturePad from "../components/SignaturePad";
import { photoPermissionService } from "../services/photoPermissionService";
import { registrationCheckService } from "../services/registrationCheckService";
import { documentService } from "../services/documentService";
import { memberService } from "../services/memberService";

function PhotoPermission() {
  const [isApproved, setIsApproved] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [memberName, setMemberName] = useState("");
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const padRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkRegistrationStatus = async () => {
      try {
        const { 
          hasMemberData, 
          hasRouteData, 
          hasPhotoData, 
          hasConfidentialityData 
        } = await registrationCheckService.checkRegistration();
        
        if (hasConfidentialityData) {
          navigate("/");
        } else if (hasPhotoData) {
          navigate("/confidentiality-agreement");
        } else if (!hasRouteData) {
          navigate("/route-check");
        } else if (!hasMemberData) {
          navigate("/member-registration");
        }
      } catch (error) {
        console.error('登録状態確認エラー:', error);
      }
    };
    
    const fetchMemberName = async () => {
      try {
        const memberData = await memberService.getRegistration();
        if (memberData?.formData?.name) {
          setMemberName(memberData.formData.name);
        }
      } catch (error) {
        console.error("氏名取得エラー:", error);
      }
    };

    // 書面本文は管理者が改訂できるため、現行の公開版をAPIから取得する
    const fetchDocument = async () => {
      try {
        setDoc(await documentService.getCurrent('photo_permission'));
      } catch (error) {
        console.error("書面取得エラー:", error);
        setError("書面の取得に失敗しました。時間をおいて再度お試しください。");
      }
    };

    checkRegistrationStatus();
    fetchMemberName();
    fetchDocument();
  }, [navigate]);

  const handleApprovalChange = (e) => {
    setIsApproved(e.target.value === "yes");
  };

  const handleSubmit = async () => {
    if (isApproved === null) {
      setError("承諾または非承諾を選択してください。");
      return;
    }
    if (isApproved && padRef.current?.isEmpty()) {
      setError("承諾する場合は署名を記入してください。");
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      await photoPermissionService.savePhotoPermission({
        isApproved,
        signatureData: padRef.current?.toDataURL(),
        documentVersionId: doc?.versionId
      });

      navigate("/confidentiality-agreement");
    } catch (err) {
      console.error("写真使用許可の保存エラー:", err);
      setError("写真使用許可の保存に失敗しました。もう一度お試しください。");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div>
        <h1 className={`${styles["text-primary"]} text-center mb-4`}>
          写真使用許可確認
        </h1>
        {doc && (
          <div className="text-end text-small text-muted mb-1">v{doc.version}</div>
        )}
        <DocumentBody
          body={doc?.body}
          values={{ name: memberName }}
          className={`${styles["description"]} mb-4`}
        />

        {/* 承諾確認 */}
        <h2 className={`${styles["section-title"]} text-center`}>
          画像使用承諾
        </h2>
        <p>
          私は、株式会社村田ソフトウェアサービスとそのグループ会社が撮影した写真及び動画を、上記の目的と方法で使用されることを
        </p>
        <small className="text-muted d-block mb-2">※どちらを選んでも登録手続きは続行されます</small>
        <div className={`${styles["approval-options"]} mb-4`}>
          <label htmlFor="approval-yes" style={{cursor:'pointer',padding:'8px 16px',display:'inline-flex',alignItems:'center',gap:8}}>
            <input
              type="radio"
              name="approval"
              id="approval-yes"
              value="yes"
              onChange={handleApprovalChange}
              required
            />
            承諾します
          </label>
          <label htmlFor="approval-no" style={{cursor:'pointer',padding:'8px 16px',display:'inline-flex',alignItems:'center',gap:8}}>
            <input
              type="radio"
              name="approval"
              id="approval-no"
              value="no"
              onChange={handleApprovalChange}
            />
            承諾しません
          </label>
        </div>

        {/* 署名エリア */}
        <div className={`${styles["signature-area"]} mb-4`}>
          <SignaturePad ref={padRef} className={styles["signature-canvas"]} />
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {/* 提出ボタン */}
        <div className={`d-grid mt-4`}>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`btn btn-primary btn-lg`}
          >
            {isSubmitting ? "送信中..." : "送信する"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PhotoPermission;
